import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Tab, Typography, useTheme, Grid } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Response as ResponseType,
  FormItem,
  ResponseSubject as SubjectResponseType,
  User,
  ResponseSubjectType,
  ItemType,
  ItemSubjectType,
  ItemRecipientType,
  Campaign,
  CampaignType,
  AssignmentSubmission,
  SubmissionType,
} from "../../types";
import { ResponseSubject } from "./Subject";
import { BlingCheckmark } from "../BlingCheckmark";
import { sortedSubjects, getUserName } from "../../DataTransform";
import { SkipNext } from "@mui/icons-material";
import { ResponsePoints } from "./Points";
import isCompatible from "../SubjectType/compatibility";
import { AssignmentSubmissionPreview } from "../Assignment/SubmissionPreview";
import { useFeatures } from "../../Contexts/FeaturesContext";
import {
  detectAdvisories,
  detectItemAdvisories,
  ItemAdvisory,
  mergeItemAdvisories,
} from "./advisories";
import { ResponseAdvisories } from "./Advisories";

// How long after the student stops typing before a model coaching check
// fires for the subject they're still looking at (FL-333 in-context rework:
// coaching must arrive while the text is on screen, not after moving on).
export const COACHING_TYPING_IDLE_MS = 2500;

export interface Props {
  response: ResponseType;
  author: User;
  subjects: User[];
  formItems: FormItem[];
  onChange: (value: ResponseType) => void;
  instructions: string;
  extraTopMargin?: boolean;
  campaignType?: CampaignType;
  hideRecipientIndicators?: boolean;
  /** Model-backed verdicts from checkResponseCoaching, keyed
   *  subjectResponseId → itemId — rendered inline under each answer. */
  modelItemAdvisories?: Record<string, Record<string, ItemAdvisory[]>>;
  /** Requests a model coaching check for a subject. Fired on typing-idle
   *  and written-answer blur (in-context), plus tab change / Next as a
   *  backstop. Content-level dedupe happens in the caller. */
  onSubjectPause?: (subjectResponseId: string) => void;
  /** Per-course opt-out (round-4): true disables ALL coaching UI for this
   *  response even when the feature flag is on. */
  coachingOptOut?: boolean;
  /** Subjects with a model check in flight — their written answers show
   *  the "thinking" shimmer while the request is pending. */
  pendingCoachingSubjects?: Record<string, boolean>;
}

export const ResponseEdit = ({
  response,
  formItems,
  subjects,
  author,
  instructions,
  onChange,
  extraTopMargin = false,
  campaignType,
  hideRecipientIndicators,
  modelItemAdvisories,
  onSubjectPause,
  coachingOptOut,
  pendingCoachingSubjects,
}: Props) => {
  const theme = useTheme();
  const { hasFeature } = useFeatures();

  // FL-333 response coaching. The panel carries only the genuinely
  // subject-level rules (straight-lining, duplicate-across-teammates);
  // everything about one specific answer renders inline under it.
  const coachingEnabled = hasFeature("response-coaching") && !coachingOptOut;
  const advisories = useMemo(
    () =>
      coachingEnabled
        ? detectAdvisories(response.responseSubjects, formItems)
        : [],
    [coachingEnabled, response.responseSubjects, formItems],
  );

  // Per-subject inline advisories: instant deterministic checks merged with
  // the model verdicts for that subject, keyed by itemId.
  const inlineAdvisories = useMemo(() => {
    if (!coachingEnabled) {
      return {} as Record<string, Record<string, ItemAdvisory[]>>;
    }
    const bySubject: Record<string, Record<string, ItemAdvisory[]>> = {};
    for (const subjectResponse of response.responseSubjects) {
      const model = modelItemAdvisories?.[subjectResponse.id] ?? {};
      bySubject[subjectResponse.id] = mergeItemAdvisories(
        detectItemAdvisories(subjectResponse, formItems, model),
        model,
      );
    }
    return bySubject;
  }, [
    coachingEnabled,
    response.responseSubjects,
    formItems,
    modelItemAdvisories,
  ]);

  // Positive recognition, per answer, in the same inline slot as the
  // coaching hints: an item is affirmed when its subject has a model
  // result (a check actually ran — degraded checks never store one), the
  // answer has real text, and nothing is flagged on it.
  const inlineAffirmations = useMemo(() => {
    const bySubject: Record<string, string[]> = {};
    if (!coachingEnabled) {
      return bySubject;
    }
    const textItemIds = formItems
      .filter((item) => item.itemType === ItemType.Text)
      .map((item) => item.id);
    for (const subjectResponse of response.responseSubjects) {
      if (!modelItemAdvisories?.[subjectResponse.id]) {
        continue;
      }
      // While a recheck is in flight the previous "checked clean" claim is
      // stale — withhold praise until the fresh verdict lands. Warnings
      // stay up (never hide criticism on spec).
      if (pendingCoachingSubjects?.[subjectResponse.id]) {
        continue;
      }
      const flaggedByItem = inlineAdvisories[subjectResponse.id] ?? {};
      bySubject[subjectResponse.id] = textItemIds.filter((itemId) => {
        const answer = subjectResponse.answers.find((a) => a.itemId === itemId);
        const text = typeof answer?.text === "string" ? answer.text.trim() : "";
        return text.length > 0 && !flaggedByItem[itemId]?.length;
      });
    }
    return bySubject;
  }, [
    coachingEnabled,
    response.responseSubjects,
    formItems,
    modelItemAdvisories,
    inlineAdvisories,
    pendingCoachingSubjects,
  ]);

  const hasNonGeneralNonPointsItems = formItems.some(
    (item) =>
      item.itemType !== ItemType.Points &&
      item.subjectType !== ItemSubjectType.General,
  );

  const sortedReponseSubjects = sortedSubjects(
    response.responseSubjects,
    author.id,
  );

  const filteredResponseSubjects = sortedSubjects(
    hasNonGeneralNonPointsItems
      ? response.responseSubjects
      : response.responseSubjects.filter(
          (rs) => rs.subjectType === ResponseSubjectType.General,
        ),
    author.id,
  );

  //
  // State
  //

  const [tab, setTab] = useState(filteredResponseSubjects[0].id);
  const [loading, setLoading] = useState(0);

  const sortedItems = [...formItems].sort((a, b) => a.order - b.order);

  // In-context trigger: when the student stops typing in this subject's
  // written answers for COACHING_TYPING_IDLE_MS, request a model check so
  // the hint appears while the text is still on screen. Blur gives the
  // immediate trigger; tab change / Next stay as backstops. On entering a
  // tab we only baseline the content — untouched answers aren't checked.
  const activeTextFingerprint = useMemo(() => {
    const subject = response.responseSubjects.find((rs) => rs.id === tab);
    if (!subject) return "";
    const textItemIds = new Set(
      formItems
        .filter((item) => item.itemType === ItemType.Text)
        .map((item) => item.id),
    );
    return JSON.stringify(
      subject.answers
        .filter((a) => textItemIds.has(a.itemId))
        .map((a) => [a.itemId, a.text ?? ""]),
    );
  }, [response.responseSubjects, formItems, tab]);
  const idleBaselines = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!coachingEnabled || !onSubjectPause) return;
    if (idleBaselines.current[tab] === undefined) {
      idleBaselines.current[tab] = activeTextFingerprint;
      return;
    }
    if (idleBaselines.current[tab] === activeTextFingerprint) return;
    const timer = setTimeout(() => {
      idleBaselines.current[tab] = activeTextFingerprint;
      onSubjectPause(tab);
    }, COACHING_TYPING_IDLE_MS);
    return () => clearTimeout(timer);
  }, [activeTextFingerprint, tab, coachingEnabled, onSubjectPause]);

  // Filter PT items by recipient type to match what the author should see,
  // using the same author-role rules as isCompatible() for non-PT items.
  const isAuthorInstructor = author.isInstructor === true;
  const pointsItems = sortedItems.filter((item) => {
    if (item.itemType !== ItemType.Points) return false;
    switch (item.recipientType) {
      case ItemRecipientType.TeamLeaders:
        return response.isTeamLead;
      case ItemRecipientType.NonLeaders:
        return !response.isTeamLead && !isAuthorInstructor;
      case ItemRecipientType.TeamInstructors:
        return isAuthorInstructor;
      case ItemRecipientType.TeamMembers:
      default:
        return !isAuthorInstructor;
    }
  });

  // For each PT item, compute the set of peer subjects that item applies to.
  // recipientType controls *who fills out* the PT item (already filtered above),
  // not who gets scored — all PT items score the same peers: every non-instructor
  // peer subject in the response.
  function getSubjectsForPointsItem(pointsItem: FormItem): User[] {
    return subjects.filter((s) => {
      const rs = sortedReponseSubjects.find((r) => r.subjectUserId === s.id);
      return rs?.subjectUserIsInstructor !== true;
    });
  }

  const isLastTab =
    pointsItems.length > 0
      ? "points" === tab
      : filteredResponseSubjects[filteredResponseSubjects.length - 1].id ===
        tab;
  const currentSubject = filteredResponseSubjects.find((sr) => sr.id === tab);

  const responsePairs = filteredResponseSubjects.map((subjectResponse) => {
    const subject = subjects.find(
      (target) => target.id === subjectResponse.subjectUserId,
    );
    return {
      subject: subject!,
      subjectResponse,
    };
  });

  // Check if this is an assignment-type campaign
  const isAssignmentCampaign = campaignType === CampaignType.Assignment;

  const isLivePresentationAssignment =
    isAssignmentCampaign &&
    response.assignment?.submissionType === SubmissionType.LivePresentation;

  // Get the current subject's assignment submission for preview
  const getCurrentSubjectSubmission = () => {
    if (
      !isAssignmentCampaign ||
      !response.assignment?.submissions ||
      !currentSubject
    ) {
      console.log("getCurrentSubjectSubmission early return:", {
        isAssignmentCampaign,
        hasAssignment: !!response.assignment,
        hasAssignmentSubmissions: !!response.assignment?.submissions,
        hasCurrentSubject: !!currentSubject,
      });
      return undefined;
    }

    console.log(
      "Looking for submission for subject:",
      currentSubject.subjectUserId,
    );
    console.log(
      "Available submissions:",
      response.assignment.submissions.map((sub) => ({
        id: sub.id,
        authorId: sub.authorId,
        submissionText: sub.submissionText?.substring(0, 50) + "...",
      })),
    );

    const submission = response.assignment.submissions.find(
      (submission) => submission.authorId === currentSubject.subjectUserId,
    );

    console.log("Found submission:", submission ? "YES" : "NO");
    return submission;
  };

  const currentSubjectSubmission = getCurrentSubjectSubmission();
  const currentSubjectUser = subjects.find(
    (user) => user.id === currentSubject?.subjectUserId,
  );

  //
  // Effects
  //

  useEffect(() => {
    const currentTabExists = filteredResponseSubjects.some(
      (subject) => subject.id === tab,
    );
    if (tab !== "points" && !currentTabExists) {
      setTab(filteredResponseSubjects[0].id);
    }
  }, [filteredResponseSubjects, tab]);

  // Auto-complete any subject tab that has zero compatible form items.
  // This can happen for surveys sent before PR 1059/1060: e.g. an instructor
  // respondent whose peer subjects have no RECIPIENT_TEAM_INSTRUCTORS items,
  // leaving them with an empty tab that can never be manually completed.
  // Mirrors the server-side check_for_completion() behaviour when
  // num_questions == 0 → completed = True.
  useEffect(() => {
    filteredResponseSubjects.forEach((subjectResponse) => {
      if (subjectResponse.completed) return;

      const compatibleItems = sortedItems.filter(
        (item) =>
          item.itemType !== ItemType.Points &&
          isCompatible(
            subjectResponse.subjectType,
            item.subjectType,
            item.recipientType,
            author,
            response.isTeamLead,
            subjectResponse,
          ),
      );

      if (compatibleItems.length === 0) {
        handleSubjectResponseChange({ ...subjectResponse, completed: true });
      }
    });
    // Intentionally omit handleSubjectResponseChange from deps — it is
    // recreated on every render and including it would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading < 200) {
      setTimeout(() => setLoading(loading + 20), 100);
    }
  }, [loading, setLoading]);

  //
  // Event handlers
  //

  function handleSubjectResponseChange(subjectResponse: SubjectResponseType) {
    const clone = { ...response };
    if (subjectResponse.completed) {
      setLoading(0);
    }
    clone.responseSubjects = clone.responseSubjects.map((target) =>
      target.id === subjectResponse.id ? subjectResponse : target,
    );
    onChange(clone);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (tab !== "points" && tab !== newValue) {
      onSubjectPause?.(tab);
    }
    setTab(newValue);
  };

  function handleNext() {
    if (tab !== "points") {
      onSubjectPause?.(tab);
    }
    const index = filteredResponseSubjects.findIndex((sr) => sr.id === tab);
    if (
      index + 1 >= filteredResponseSubjects.length &&
      pointsItems.length > 0
    ) {
      setTab("points");
    } else {
      setTab(filteredResponseSubjects[index + 1].id);
    }

    // Scroll to page top after tab change
    setTimeout(() => window.scroll({ top: 0, left: 0, behavior: "smooth" }), 0);
  }

  //
  // Helpers
  //

  function getName(pair: {
    subject: User;
    subjectResponse: SubjectResponseType;
  }) {
    const { subject: user, subjectResponse } = pair;

    if (subjectResponse.subjectType === ResponseSubjectType.General) {
      return "General Reflection";
    }

    return getUserName(user) + (user.id === author.id ? " (Yourself)" : "");
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <TabContext value={tab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              position: "fixed",
              top: extraTopMargin ? "90px" : "45px",
              left: "0px",
              backgroundColor: theme.palette.background.default,
              width: "100%",
              zIndex: 2,
            }}
          >
            <TabList
              value={tab}
              onChange={handleTabChange}
              aria-label="basic tabs example"
            >
              {filteredResponseSubjects.map((subjectResponse, index) => {
                const pair = {
                  subject: subjects.find(
                    (target) => target.id === subjectResponse.subjectUserId,
                  )!,
                  subjectResponse,
                };

                return (
                  <Tab
                    disabled={
                      isLivePresentationAssignment
                        ? false
                        : !pair.subjectResponse.completed &&
                          index >
                            filteredResponseSubjects.reduce(
                              (acc, response) =>
                                acc + (response.completed ? 1 : 0),
                              0,
                            )
                    }
                    key={index}
                    label={
                      isLivePresentationAssignment
                        ? getName(pair)
                        : pair.subjectResponse.completed ||
                            index <=
                              filteredResponseSubjects.reduce(
                                (acc, response) =>
                                  acc + (response.completed ? 1 : 0),
                                0,
                              )
                          ? getName(pair)
                          : ""
                    }
                    value={pair.subjectResponse.id}
                    icon={
                      <BlingCheckmark
                        active={
                          tab === pair.subjectResponse.id &&
                          !pair.subjectResponse.completed
                        }
                        user={pair.subject}
                        completed={pair.subjectResponse.completed}
                        divProps={{ style: { marginRight: 10 } }}
                        size="small"
                      />
                    }
                    iconPosition="start"
                  />
                );
              })}
              {pointsItems.length > 0 && (
                <Tab
                  disabled={
                    filteredResponseSubjects.length >
                    filteredResponseSubjects.reduce(
                      (acc, response) => acc + (response.completed ? 1 : 0),
                      0,
                    )
                  }
                  label={
                    filteredResponseSubjects.length <=
                    filteredResponseSubjects.reduce(
                      (acc, response) => acc + (response.completed ? 1 : 0),
                      0,
                    )
                      ? "Points"
                      : ""
                  }
                  value="points"
                  icon={
                    <BlingCheckmark
                      active={tab === "points"}
                      completed={false}
                      divProps={{ style: { marginRight: 10 } }}
                      size="small"
                    />
                  }
                  iconPosition="start"
                />
              )}
            </TabList>
          </Box>
          <Box sx={{ mt: 8 }}>&nbsp;</Box>
          {/* stickyTop = fixed tab bar top (45/90px) + 72px bar height + 4px gap,
              so advisories stay visible while scrolling a long form. */}
          <ResponseAdvisories
            advisories={advisories}
            stickyTop={extraTopMargin ? "166px" : "121px"}
          />
          {filteredResponseSubjects.map((subjectResponse, index) => (
            <TabPanel key={index} value={subjectResponse.id}>
              {isAssignmentCampaign ? (
                // Two-column layout for assignment campaigns
                <Grid container spacing={3}>
                  {/* Left column: Assignment Submission Preview */}
                  <Grid item xs={12} md={7}>
                    <AssignmentSubmissionPreview
                      submission={currentSubjectSubmission}
                      authorName={
                        currentSubjectUser
                          ? getUserName(currentSubjectUser)
                          : "Student"
                      }
                    />
                  </Grid>

                  {/* Right column: Response Form */}
                  <Grid item xs={12} md={5}>
                    {subjectResponse.subjectType !==
                      ResponseSubjectType.General && (
                      <>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                          Instructions:
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                          {instructions.split("\n").map((x, index, arr) => (
                            <>
                              {x}
                              {index !== arr.length - 1 && <br />}
                            </>
                          ))}
                        </Typography>
                      </>
                    )}
                    <ResponseSubject
                      subjectResponse={subjectResponse}
                      author={author}
                      subject={
                        subjects.find(
                          (target) =>
                            target.id === subjectResponse.subjectUserId,
                        )!
                      }
                      onChange={handleSubjectResponseChange}
                      formItems={sortedItems.filter(
                        (item) =>
                          item.itemType !== ItemType.Points &&
                          isCompatible(
                            subjectResponse.subjectType,
                            item.subjectType,
                            item.recipientType,
                            author,
                            response.isTeamLead,
                            subjectResponse,
                          ),
                      )}
                      hideRecipientIndicators={hideRecipientIndicators}
                      itemAdvisories={inlineAdvisories[subjectResponse.id]}
                      affirmedItemIds={inlineAffirmations[subjectResponse.id]}
                      coachingPending={
                        coachingEnabled &&
                        !!pendingCoachingSubjects?.[subjectResponse.id]
                      }
                      onTextBlur={() => onSubjectPause?.(subjectResponse.id)}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Original single-column layout for non-assignment campaigns
                <>
                  {subjectResponse.subjectType !==
                    ResponseSubjectType.General && (
                    <>
                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Instructions:
                      </Typography>
                      <Typography sx={{ mb: 2 }}>
                        {instructions.split("\n").map((x, index, arr) => (
                          <>
                            {x}
                            {index !== arr.length - 1 && <br />}
                          </>
                        ))}
                      </Typography>
                    </>
                  )}
                  <ResponseSubject
                    subjectResponse={subjectResponse}
                    author={author}
                    subject={
                      subjects.find(
                        (target) => target.id === subjectResponse.subjectUserId,
                      )!
                    }
                    onChange={handleSubjectResponseChange}
                    formItems={sortedItems.filter(
                      (item) =>
                        item.itemType !== ItemType.Points &&
                        isCompatible(
                          subjectResponse.subjectType,
                          item.subjectType,
                          item.recipientType,
                          author,
                          response.isTeamLead,
                          subjectResponse,
                        ),
                    )}
                    hideRecipientIndicators={hideRecipientIndicators}
                    itemAdvisories={inlineAdvisories[subjectResponse.id]}
                    affirmedItemIds={inlineAffirmations[subjectResponse.id]}
                    coachingPending={
                      coachingEnabled &&
                      !!pendingCoachingSubjects?.[subjectResponse.id]
                    }
                    onTextBlur={() => onSubjectPause?.(subjectResponse.id)}
                  />
                </>
              )}
            </TabPanel>
          ))}
          {pointsItems.length > 0 && (
            <TabPanel value="points">
              {pointsItems.map((pointsItem) => (
                <ResponsePoints
                  onChange={handleSubjectResponseChange}
                  formItem={pointsItem}
                  key={pointsItem.id}
                  subjects={getSubjectsForPointsItem(pointsItem)}
                  responseSubjects={sortedReponseSubjects}
                />
              ))}
            </TabPanel>
          )}
        </TabContext>
      </Box>
      <Button
        disabled={!currentSubject?.completed || isLastTab}
        variant="contained"
        endIcon={<SkipNext />}
        onClick={handleNext}
        sx={{ ml: 3 }}
      >
        Next
      </Button>
    </>
  );
};
